import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import './FourPointRiggingSpreaderBar.css';
import image2 from '../../images/four-point-spreader/image2.png';
import image3 from '../../images/four-point-spreader/image3.png';


export default function FourPointRiggingSpreaderBar() {
    // Inputs
    const [W, setW] = useState(30);
    const [SW, setSW] = useState(2);

    const [D1, setD1] = useState(4.000);
    const [D2, setD2] = useState(4.500);
    const [D3, setD3] = useState(2.000);
    const [D4, setD4] = useState(1.500);

    const [DT1, setDT1] = useState(8.500);
    const [DT2, setDT2] = useState(3.500);

    const [S1, setS1] = useState(2.000);
    const [S2, setS2] = useState(6.000);
    const [S3, setS3] = useState(6.500);

    const [RW1, setRW1] = useState(0.200);
    const [RW2, setRW2] = useState(0.200);
    const [RW3, setRW3] = useState(0.165);
    const [RW4, setRW4] = useState(0.165);

    // Results State
    const [results, setResults] = useState({
        L1: 0, L2: 0, L3: 0, L4: 0,
        T1: 0, T2: 0, T3: 0, T4: 0,
        SW12: 0, SW34: 0,
        TW1: 0, TW2: 0,
        H1: 0, H2: 0,
        T5: 0, T6: 0
    });

    const [theme, setTheme] = useState('dark');
    const heroCanvasRef = useRef(null);
    const rigCanvasRef = useRef(null);

    // Sync offsets when D changes: DT1 = D1 + D2, DT2 = D3 + D4
    const handleDChange = (name, value) => {
        const val = parseFloat(value) || 0;
        if (name === 'D1') {
            setD1(val);
            setDT1(Number((val + D2).toFixed(3)));
        } else if (name === 'D2') {
            setD2(val);
            setDT1(Number((D1 + val).toFixed(3)));
        } else if (name === 'D3') {
            setD3(val);
            setDT2(Number((val + D4).toFixed(3)));
        } else if (name === 'D4') {
            setD4(val);
            setDT2(Number((D3 + val).toFixed(3)));
        }
    };

    // Sync offsets when total span changes (updates D2 or D4)
    const handleDTChange = (name, value) => {
        const val = parseFloat(value) || 0;
        if (name === 'DT1') {
            setDT1(val);
            setD2(Number((val - D1).toFixed(3)));
        } else if (name === 'DT2') {
            setDT2(val);
            setD4(Number((val - D3).toFixed(3)));
        }
    };

    // Theme Listener
    useEffect(() => {
        const initialTheme = document.body.getAttribute('data-theme') || 'dark';
        setTheme(initialTheme);

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.attributeName === 'data-theme') {
                    const newTheme = document.body.getAttribute('data-theme') || 'dark';
                    setTheme(newTheme);
                }
            });
        });

        observer.observe(document.body, { attributes: true });
        return () => observer.disconnect();
    }, []);

    // Perform Calculations (based on Excel sheet formulas)
    useEffect(() => {
        const dt1Val = DT1 || 1.0;
        const dt2Val = DT2 || 1.0;

        // 1. Cargo Loads (Moment Balance Inverse Lever Rule)
        const L1 = W * (D2 / dt1Val) * (D3 / dt2Val);
        const L2 = W * (D2 / dt1Val) * (D4 / dt2Val);
        const L3 = W * (D1 / dt1Val) * (D4 / dt2Val);
        const L4 = W * (D1 / dt1Val) * (D3 / dt2Val);

        // Lower Sling Tensions (S1 is vertical in this model)
        const T1 = L1;
        const T2 = L2;
        const T3 = L3;
        const T4 = L4;

        // 2. Spreader Bar Weight Distribution
        const SW12 = SW * (D2 / dt1Val);
        const SW34 = SW * (D1 / dt1Val);

        // 3. Upper Sling Vertical Loads (TW1 = Side A, TW2 = Side B)
        // Side A = LP1/LP2 side (uses RW1 - estimate weight)
        // Side B = LP3/LP4 side (uses RW2 - estimate weight)
        const TW1 = T1 + T2 + SW12 + RW1;
        const TW2 = T3 + T4 + SW34 + RW2;

        // 4. Upper Sling Heights
        const H1 = S2 > D1 ? Math.sqrt(S2 * S2 - D1 * D1) : 0;
        const H2 = S3 > D2 ? Math.sqrt(S3 * S3 - D2 * D2) : 0;

        // Upper sling tensions
        const T5 = TW1 * (H1 > 0 ? (S2 / H1) : 0);
        const T6 = TW2 * (H2 > 0 ? (S3 / H2) : 0);

        setResults({
            L1: Number(L1.toFixed(3)),
            L2: Number(L2.toFixed(3)),
            L3: Number(L3.toFixed(3)),
            L4: Number(L4.toFixed(3)),
            T1: Number(T1.toFixed(3)),
            T2: Number(T2.toFixed(3)),
            T3: Number(T3.toFixed(3)),
            T4: Number(T4.toFixed(3)),
            SW12: Number(SW12.toFixed(3)),
            SW34: Number(SW34.toFixed(3)),
            TW1: Number(TW1.toFixed(3)),
            TW2: Number(TW2.toFixed(3)),
            H1: Number(H1.toFixed(3)),
            H2: Number(H2.toFixed(3)),
            T5: Number(T5.toFixed(3)),
            T6: Number(T6.toFixed(3))
        });
    }, [W, SW, D1, D2, D3, D4, DT1, DT2, S1, S2, S3, RW1, RW2, RW3, RW4]);

    // Draw the 3D Rigging System Schematic dynamically on canvas elements
    useEffect(() => {
        const drawSchematic = (canvasRef) => {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const ctx = canvas.getContext('2d');

            // Adjust for High-DPI screens
            const dpr = window.devicePixelRatio || 1;
            const width = canvas.clientWidth;
            const height = canvas.clientHeight;
            if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
                canvas.width = width * dpr;
                canvas.height = height * dpr;
            }
            ctx.resetTransform();
            ctx.scale(dpr, dpr);
            ctx.clearRect(0, 0, width, height);

            // Theme colors
            const isDark = theme === 'dark';
            const gridColor = isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)';
            const upperSlingColor = '#3E8BFF'; // Blue
            const lowerSlingColor = '#06B6D4'; // Cyan
            const cogColor = '#EF4444'; // Red

            // Base HSL colors for 3D elements
            const spreaderBaseColor = { h: 162, s: 84, l: 38 }; // emerald green
            const cargoBaseColor = isDark ? { h: 215, s: 20, l: 30 } : { h: 35, s: 30, l: 65 }; // warm beige/slate

            // Background Grid
            ctx.strokeStyle = gridColor;
            ctx.lineWidth = 1;
            const gridSize = 25;
            for (let x = 0; x < width; x += gridSize) {
                ctx.beginPath();
                ctx.moveTo(x, 0);
                ctx.lineTo(x, height);
                ctx.stroke();
            }
            for (let y = 0; y < height; y += gridSize) {
                ctx.beginPath();
                ctx.moveTo(0, y);
                ctx.lineTo(width, y);
                ctx.stroke();
            }

            // Isometric projection constants
            const cx = width / 2;
            const cy = height * 0.72; // center-bottom shifted down to make room for hook
            const scale = Math.min(width / 16, height / 14);

            const angleX = -Math.PI / 10;
            const angleY = Math.PI / 6;

            const project = (x, y, z) => {
                const px = cx + (x * Math.cos(angleX) - y * Math.cos(angleY)) * scale;
                const py = cy - (z + x * Math.sin(angleX) + y * Math.sin(angleY)) * scale;
                return { x: px, y: py };
            };

            // 3D drawing helper functions
            const drawFace = (points, fillColor, strokeColor) => {
                ctx.fillStyle = fillColor;
                ctx.strokeStyle = strokeColor || 'rgba(0,0,0,0.12)';
                ctx.lineWidth = 1;
                ctx.beginPath();
                const pt0 = project(points[0].x, points[0].y, points[0].z);
                ctx.moveTo(pt0.x, pt0.y);
                for (let i = 1; i < points.length; i++) {
                    const pt = project(points[i].x, points[i].y, points[i].z);
                    ctx.lineTo(pt.x, pt.y);
                }
                ctx.closePath();
                ctx.fill();
                ctx.stroke();
            };

            const draw3DBox = (x1, y1, z1, x2, y2, z2, baseColorHSL) => {
                const minX = Math.min(x1, x2);
                const maxX = Math.max(x1, x2);
                const minY = Math.min(y1, y2);
                const maxY = Math.max(y1, y2);
                const minZ = Math.min(z1, z2);
                const maxZ = Math.max(z1, z2);

                const faces = [
                    {
                        name: 'bottom',
                        pts: [
                            { x: minX, y: minY, z: minZ },
                            { x: maxX, y: minY, z: minZ },
                            { x: maxX, y: maxY, z: minZ },
                            { x: minX, y: maxY, z: minZ }
                        ],
                        cx: (minX + maxX) / 2, cy: (minY + maxY) / 2, cz: minZ
                    },
                    {
                        name: 'top',
                        pts: [
                            { x: minX, y: minY, z: maxZ },
                            { x: maxX, y: minY, z: maxZ },
                            { x: maxX, y: maxY, z: maxZ },
                            { x: minX, y: maxY, z: maxZ }
                        ],
                        cx: (minX + maxX) / 2, cy: (minY + maxY) / 2, cz: maxZ
                    },
                    {
                        name: 'back',
                        pts: [
                            { x: minX, y: maxY, z: minZ },
                            { x: maxX, y: maxY, z: minZ },
                            { x: maxX, y: maxY, z: maxZ },
                            { x: minX, y: maxY, z: maxZ }
                        ],
                        cx: (minX + maxX) / 2, cy: maxY, cz: (minZ + maxZ) / 2
                    },
                    {
                        name: 'front',
                        pts: [
                            { x: minX, y: minY, z: minZ },
                            { x: maxX, y: minY, z: minZ },
                            { x: maxX, y: minY, z: maxZ },
                            { x: minX, y: minY, z: maxZ }
                        ],
                        cx: (minX + maxX) / 2, cy: minY, cz: (minZ + maxZ) / 2
                    },
                    {
                        name: 'left',
                        pts: [
                            { x: minX, y: minY, z: minZ },
                            { x: minX, y: maxY, z: minZ },
                            { x: minX, y: maxY, z: maxZ },
                            { x: minX, y: minY, z: maxZ }
                        ],
                        cx: minX, cy: (minY + maxY) / 2, cz: (minZ + maxZ) / 2
                    },
                    {
                        name: 'right',
                        pts: [
                            { x: maxX, y: minY, z: minZ },
                            { x: maxX, y: maxY, z: minZ },
                            { x: maxX, y: maxY, z: maxZ },
                            { x: maxX, y: minY, z: maxZ }
                        ],
                        cx: maxX, cy: (minY + maxY) / 2, cz: (minZ + maxZ) / 2
                    }
                ];

                // Sort faces by depth: depth = -cx * 0.3 + cy * 0.9 - cz * 0.5 (Painter's algorithm)
                faces.forEach(f => {
                    f.depth = -f.cx * 0.3 + f.cy * 0.9 - f.cz * 0.5;
                });

                faces.sort((a, b) => b.depth - a.depth);

                faces.forEach(f => {
                    let lAdjust = 0;
                    if (f.name === 'top') lAdjust = 12;
                    else if (f.name === 'front') lAdjust = -2;
                    else if (f.name === 'right') lAdjust = -6;
                    else if (f.name === 'left') lAdjust = -12;
                    else if (f.name === 'back') lAdjust = -18;
                    else if (f.name === 'bottom') lAdjust = -22;

                    const h = baseColorHSL.h;
                    const s = baseColorHSL.s;
                    const l = Math.max(5, Math.min(95, baseColorHSL.l + lAdjust));
                    const fillColor = `hsl(${h}, ${s}%, ${l}%)`;
                    const strokeColor = isDark
                        ? `hsl(${h}, ${s}%, ${Math.min(95, l + 8)}%)`
                        : `hsl(${h}, ${s}%, ${Math.max(5, l - 8)}%)`;

                    drawFace(f.pts, fillColor, strokeColor);
                });
            };

            const drawCable = (p1, p2, color, width) => {
                const pt1 = project(p1.x, p1.y, p1.z);
                const pt2 = project(p2.x, p2.y, p2.z);

                // Cable dark outline shadow
                ctx.strokeStyle = isDark ? '#090d16' : '#1e293b';
                ctx.lineWidth = width + 2;
                ctx.lineCap = 'round';
                ctx.beginPath();
                ctx.moveTo(pt1.x, pt1.y);
                ctx.lineTo(pt2.x, pt2.y);
                ctx.stroke();

                // Cable core body
                ctx.strokeStyle = color;
                ctx.lineWidth = width;
                ctx.beginPath();
                ctx.moveTo(pt1.x, pt1.y);
                ctx.lineTo(pt2.x, pt2.y);
                ctx.stroke();

                // 3D Cable center highlight
                ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
                ctx.lineWidth = width * 0.3;
                ctx.beginPath();
                ctx.moveTo(pt1.x, pt1.y);
                ctx.lineTo(pt2.x, pt2.y);
                ctx.stroke();
            };

            const drawShackle = (p) => {
                const pt = project(p.x, p.y, p.z);
                ctx.strokeStyle = isDark ? '#94A3B8' : '#475569';
                ctx.fillStyle = isDark ? '#475569' : '#CBD5E1';
                ctx.lineWidth = 1.8;

                // Draw shackle loop
                ctx.beginPath();
                ctx.arc(pt.x, pt.y, 4, 0, Math.PI * 2);
                ctx.fill();
                ctx.stroke();

                // Draw shackle red pin
                ctx.fillStyle = '#EF4444';
                ctx.beginPath();
                ctx.arc(pt.x, pt.y, 1.5, 0, Math.PI * 2);
                ctx.fill();
            };

            const drawPadeye = (p) => {
                const pw = 0.06;
                const pl = 0.15;
                const ph = 0.12;
                draw3DBox(p.x - pl / 2, p.y - pw / 2, p.z, p.x + pl / 2, p.y + pw / 2, p.z + ph, isDark ? { h: 210, s: 15, l: 45 } : { h: 210, s: 15, l: 65 });
            };

            const getMidPoint = (p1, p2, ratio = 0.5) => {
                return {
                    x: p1.x + (p2.x - p1.x) * ratio,
                    y: p1.y + (p2.y - p1.y) * ratio,
                    z: p1.z + (p2.z - p1.z) * ratio
                };
            };

            // Structure heights
            const hCargo = 1.0;
            const zSpreader = hCargo + S1;
            const avgH = (results.H1 + results.H2) / 2 || 4.5;
            const zHook = zSpreader + avgH;

            // Define 3D Coordinate points
            const ptHook = { x: 0, y: 0, z: zHook };
            const ptSpreaderLeft = { x: -D1, y: 0, z: zSpreader };
            const ptSpreaderRight = { x: D2, y: 0, z: zSpreader };

            const ptSpreaderLeft_front = { x: -D1, y: -D3, z: zSpreader };
            const ptSpreaderLeft_back = { x: -D1, y: D4, z: zSpreader };
            const ptSpreaderRight_front = { x: D2, y: -D3, z: zSpreader };
            const ptSpreaderRight_back = { x: D2, y: D4, z: zSpreader };

            const ptLP1 = { x: -D1, y: -D3, z: hCargo };
            const ptLP2 = { x: -D1, y: D4, z: hCargo };
            const ptLP3 = { x: D2, y: -D3, z: hCargo };
            const ptLP4 = { x: D2, y: D4, z: hCargo };

            const w_wall = 0.25; // wall thickness of U-channel
            const t_floor = 0.20; // base floor thickness of U-channel

            // --- DRAW LAYER 1: Back lower slings ---
            // Sling 2 (LP2)
            drawCable(ptLP2, ptSpreaderLeft_back, lowerSlingColor, 2.5);
            // Sling 4 (LP4)
            drawCable(ptLP4, ptSpreaderRight_back, lowerSlingColor, 2.5);

            // --- DRAW LAYER 2: Cargo Base & Back Wall ---
            // Bottom floor of cargo
            draw3DBox(-D1, -D3, 0, D2, D4, t_floor, cargoBaseColor);
            // Back wall of cargo
            draw3DBox(-D1, D4 - w_wall, t_floor, D2, D4, hCargo, cargoBaseColor);

            // --- DRAW LAYER 3: COG Target inside U-channel ---
            const ptCOG = project(0, 0, t_floor);
            ctx.strokeStyle = cogColor;
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.arc(ptCOG.x, ptCOG.y, 6, 0, Math.PI * 2);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(ptCOG.x - 10, ptCOG.y);
            ctx.lineTo(ptCOG.x + 10, ptCOG.y);
            ctx.moveTo(ptCOG.x, ptCOG.y - 10);
            ctx.lineTo(ptCOG.x, ptCOG.y + 10);
            ctx.stroke();
            ctx.fillStyle = cogColor;
            ctx.beginPath();
            ctx.arc(ptCOG.x, ptCOG.y, 2, 0, Math.PI * 2);
            ctx.fill();

            // --- DRAW LAYER 4: Spreader Bar H-Beam and transverse endbars ---
            // Main Longitudinal Spreader Beam (built as H-Beam stack)
            // Bottom flange
            draw3DBox(-D1, -0.12, zSpreader - 0.12, D2, 0.12, zSpreader - 0.08, spreaderBaseColor);
            // Web
            draw3DBox(-D1, -0.03, zSpreader - 0.08, D2, 0.03, zSpreader + 0.08, spreaderBaseColor);
            // Top flange
            draw3DBox(-D1, -0.12, zSpreader + 0.08, D2, 0.12, zSpreader + 0.12, spreaderBaseColor);

            // Left Transverse End Bar (at X = -D1)
            draw3DBox(-D1 - 0.12, -D3, zSpreader - 0.10, -D1 + 0.12, D4, zSpreader + 0.10, spreaderBaseColor);
            // Right Transverse End Bar (at X = D2)
            draw3DBox(D2 - 0.12, -D3, zSpreader - 0.10, D2 + 0.12, D4, zSpreader + 0.10, spreaderBaseColor);

            // Shackles on Spreader Bar (lower attachments)
            drawShackle(ptSpreaderLeft_front);
            drawShackle(ptSpreaderLeft_back);
            drawShackle(ptSpreaderRight_front);
            drawShackle(ptSpreaderRight_back);

            // Shackles on Spreader Bar (upper attachments)
            drawShackle(ptSpreaderLeft);
            drawShackle(ptSpreaderRight);

            // Padeyes on Cargo Top
            drawPadeye(ptLP1);
            drawPadeye(ptLP2);
            drawPadeye(ptLP3);
            drawPadeye(ptLP4);

            // Shackles on Cargo Top
            drawShackle(ptLP1);
            drawShackle(ptLP2);
            drawShackle(ptLP3);
            drawShackle(ptLP4);

            // --- DRAW LAYER 5: Crane Hook & Master Link ---
            const hookPt2D = project(ptHook.x, ptHook.y, ptHook.z);
            // Master link ring
            ctx.strokeStyle = isDark ? '#94A3B8' : '#475569';
            ctx.fillStyle = isDark ? '#475569' : '#CBD5E1';
            ctx.lineWidth = 2.5;
            ctx.beginPath();
            ctx.ellipse(hookPt2D.x, hookPt2D.y + 6, 8, 14, 0, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();

            // Crane hook shank & hook loop
            ctx.strokeStyle = isDark ? '#F1F5F9' : '#1E293B';
            ctx.lineWidth = 4;
            ctx.lineCap = 'round';
            ctx.beginPath();
            ctx.moveTo(hookPt2D.x, hookPt2D.y - 25);
            ctx.lineTo(hookPt2D.x, hookPt2D.y - 12);
            ctx.lineTo(hookPt2D.x, hookPt2D.y - 5);
            ctx.arc(hookPt2D.x + 6, hookPt2D.y - 5, 7, Math.PI, Math.PI * 0.45, true);
            ctx.stroke();

            // Safety latch (red clip)
            ctx.strokeStyle = '#EF4444';
            ctx.lineWidth = 1.2;
            ctx.beginPath();
            ctx.moveTo(hookPt2D.x, hookPt2D.y - 8);
            ctx.lineTo(hookPt2D.x + 10, hookPt2D.y - 2);
            ctx.stroke();

            // Upper slings
            drawCable(ptSpreaderLeft, ptHook, upperSlingColor, 3.2);
            drawCable(ptSpreaderRight, ptHook, upperSlingColor, 3.2);

            // --- DRAW LAYER 6: Front cargo wall ---
            draw3DBox(-D1, -D3, t_floor, D2, -D3 + w_wall, hCargo, cargoBaseColor);

            // --- DRAW LAYER 7: Front lower slings ---
            // Sling 1 (LP1)
            drawCable(ptLP1, ptSpreaderLeft_front, lowerSlingColor, 2.5);
            // Sling 3 (LP3)
            drawCable(ptLP3, ptSpreaderRight_front, lowerSlingColor, 2.5);

            // --- DRAW LAYER 8: Labels & Leader Lines (No values, names only) ---
            const drawLabelBox = (text, x, y, borderColor) => {
                ctx.font = 'bold 9px sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';

                const txtW = ctx.measureText(text).width + 12;
                const txtH = 18;

                ctx.fillStyle = isDark ? '#1e293b' : '#ffffff';
                ctx.beginPath();
                ctx.roundRect(x - txtW / 2, y - txtH / 2, txtW, txtH, 4);
                ctx.fill();

                ctx.strokeStyle = borderColor || (isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)');
                ctx.lineWidth = borderColor ? 1.5 : 1;
                ctx.stroke();

                ctx.fillStyle = isDark ? '#f8fafc' : '#0f172a';
                ctx.fillText(text, x, y);
            };

            const drawLabelWithLeader = (text, anchorPt3D, offset2D, borderColor) => {
                const anchorPt2D = project(anchorPt3D.x, anchorPt3D.y, anchorPt3D.z);
                const labelPt2D = {
                    x: anchorPt2D.x + offset2D.x,
                    y: anchorPt2D.y + offset2D.y
                };

                // Draw line from anchor to label
                ctx.strokeStyle = borderColor || (isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)');
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(anchorPt2D.x, anchorPt2D.y);
                ctx.lineTo(labelPt2D.x, labelPt2D.y);
                ctx.stroke();

                // Anchor dot
                ctx.fillStyle = borderColor || (isDark ? '#f8fafc' : '#0f172a');
                ctx.beginPath();
                ctx.arc(anchorPt2D.x, anchorPt2D.y, 2.5, 0, Math.PI * 2);
                ctx.fill();

                drawLabelBox(text, labelPt2D.x, labelPt2D.y, borderColor);
            };

            // Midpoints for upper/lower slings
            const midS5 = getMidPoint(ptSpreaderLeft, ptHook, 0.45);
            const midS6 = getMidPoint(ptSpreaderRight, ptHook, 0.45);
            const midS1 = getMidPoint(ptLP1, ptSpreaderLeft_front, 0.5);
            const midS2 = getMidPoint(ptLP2, ptSpreaderLeft_back, 0.5);
            const midS3 = getMidPoint(ptLP3, ptSpreaderRight_front, 0.5);
            const midS4 = getMidPoint(ptLP4, ptSpreaderRight_back, 0.5);

            // Sling label boxes with leaders (colored borders matching the slings, names only)
            drawLabelWithLeader('Sling 5 [T5]', midS5, { x: -60, y: -15 }, upperSlingColor);
            drawLabelWithLeader('Sling 6 [T6]', midS6, { x: 60, y: -15 }, upperSlingColor);

            drawLabelWithLeader('Sling 1 [T1]', midS1, { x: -55, y: 15 }, lowerSlingColor);
            drawLabelWithLeader('Sling 2 [T2]', midS2, { x: -55, y: -20 }, lowerSlingColor);
            drawLabelWithLeader('Sling 3 [T3]', midS3, { x: 55, y: 20 }, lowerSlingColor);
            drawLabelWithLeader('Sling 4 [T4]', midS4, { x: 55, y: -10 }, lowerSlingColor);

            // Component Labels (neutral borders, names only)
            const ptSpreaderMid = project((D2 - D1) / 2, 0, zSpreader);
            drawLabelBox('Spreader Bar', ptSpreaderMid.x, ptSpreaderMid.y - 25, false);

            const ptCargoLabel = project((D2 - D1) / 2, -D3, hCargo);
            drawLabelBox('Cargo', ptCargoLabel.x, ptCargoLabel.y + 20, false);

            // COG Text above the crosshair
            const ptCOGLabel = project(0, 0, t_floor + 0.25);
            ctx.fillStyle = cogColor;
            ctx.font = 'bold 9px sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('COG', ptCOGLabel.x, ptCOGLabel.y);
        };

        drawSchematic(heroCanvasRef);
        drawSchematic(rigCanvasRef);

        const handleResize = () => {
            drawSchematic(heroCanvasRef);
            drawSchematic(rigCanvasRef);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);

    }, [W, SW, D1, D2, D3, D4, DT1, DT2, S1, S2, S3, results, theme]);


    return (
        <div className="spreader-bar-calculator">
            <Helmet>
                <title>4-Point Rigging with Spreader Bar — Engineering Calculator</title>
                <meta name="description" content="Calculate sling tensions, load distribution, and geometry for spreader bar rigging systems with asymmetric COG. Real-time engineering dashboard." />
            </Helmet>

            <div className="bp-bg"></div>

            {/* HERO SECTION */}
            <div style={{ maxWidth: '1300px', margin: '0 auto', padding: '0 2.5rem' }}>
                <div className="hero">
                    <div className="hero-left">
                        <div className="hero-badge">Structural Rigging Analysis Tool v2.0</div>
                        <h1 className="hero-title">
                            4-Point Lifting With<br />
                            <span className="line2">Spreader Bar</span><br />
                            <span className="line3">Calculator</span>
                        </h1>
                        <p className="hero-desc">
                            Advanced 6-sling rigging analysis for asymmetric heavy lifts with spreader bar systems.
                            Computes load distribution, sling tensions below and above the spreader bar,
                            and geometric heights in real time.
                        </p>
                        <div className="hero-actions">
                            <a href="#calculator" className="btn-fill">Launch Calculator</a>
                            <a href="#system" className="btn-ghost">System Overview</a>
                        </div>
                    </div>
                    <div className="hero-schematic">
                        <div className="schematic-box">
                            <div className="schematic-label">Rigging System 3D Schematic</div>
                            <canvas ref={heroCanvasRef} style={{ width: '100%', height: '350px', display: 'block', borderRadius: '8px', background: 'var(--bg2)', border: '1px solid var(--border)' }}></canvas>
                        </div>
                    </div>
                </div>
            </div>

            {/* KPI STRIP */}
            <div className="kpi-strip">
                <div className="kpi-inner">
                    <div className="kpi-item"><div className="kpi-num">6</div><div className="kpi-lbl">Total Slings</div></div>
                    <div className="kpi-item"><div className="kpi-num">4</div><div className="kpi-lbl">Lifting Points</div></div>
                    <div className="kpi-item"><div className="kpi-num">2</div><div className="kpi-lbl">Upper Slings</div></div>
                    <div className="kpi-item"><div className="kpi-num">1</div><div className="kpi-lbl">Spreader Bar</div></div>
                    <div className="kpi-item"><div className="kpi-num">&plusmn;0.001</div><div className="kpi-lbl">Precision (t)</div></div>
                    <div className="kpi-item"><div className="kpi-num">Live</div><div className="kpi-lbl">Calculation</div></div>
                </div>
            </div>

            {/* SYSTEM OVERVIEW */}
            <div className="section" id="system">
                <div className="sec-eyebrow">System Overview</div>
                <h2 className="sec-title">Spreader Bar<br />Rigging System</h2>
                <p className="sec-sub">A spreader bar system uses a rigid horizontal beam between the cargo and crane hook. Four lower slings connect the cargo to the spreader bar; two upper slings connect the spreader bar to the crane. This keeps sling angles vertical and reduces horizontal forces on lifting lugs.</p>

                <div className="system-grid">
                    <div className="system-card">
                        <div className="sys-num">01</div>
                        <div className="sys-icon">▣</div>
                        <h3>Lower Slings (S1) — 4 Legs</h3>
                        <p>Four slings run from the cargo lifting points up to the spreader bar attachment points. All four lower slings share the same length S1. The load at each lifting point is determined by the COG offset distances D1–D4 using the inverse lever rule applied in two directions.</p>
                    </div>
                    <div className="system-card">
                        <div className="sys-num">02</div>
                        <div className="sys-icon">▬</div>
                        <h3>Spreader Bar — SW</h3>
                        <p>The spreader bar is a rigid steel beam that separates the upper slings from the lower sling attachment points. Its weight (SW) is distributed to each side in proportion to the COG's longitudinal position. It eliminates inward horizontal force components on the cargo lifting lugs.</p>
                    </div>
                    <div className="system-card">
                        <div className="sys-num">03</div>
                        <div className="sys-icon">↑</div>
                        <h3>Upper Slings (S2, S3) — 2 Legs</h3>
                        <p>Two upper slings (Sling 5 and Sling 6) run from each end of the spreader bar up to the crane hook. S2 serves the LP1/LP2 side; S3 serves the LP3/LP4 side. The sling angle factor (S/H) amplifies vertical load into actual sling tension.</p>
                    </div>
                    <div className="system-card">
                        <div className="sys-num">04</div>
                        <div className="sys-icon">◎</div>
                        <h3>COG Eccentricity Handling</h3>
                        <p>When the centre of gravity does not coincide with the geometric centre of the lift, loads are distributed unevenly across all four points. This calculator solves the full 2D asymmetric distribution, accounting for both longitudinal (D1/D2) and lateral (D3/D4) COG offsets simultaneously.</p>
                    </div>
                </div>
            </div>

            {/* INTERACTIVE CALCULATOR */}
            <div id="calculator" style={{ background: 'var(--bg2)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
                <div className="calc-section" style={{ maxWidth: '1300px', margin: '0 auto', padding: '5.5rem 2.5rem' }}>
                    <div className="sec-eyebrow">Interactive Tool</div>
                    <h2 className="sec-title">Live Engineering<br />Calculator</h2>
                    <p className="sec-sub">Pre-loaded with sample data: 30-tonne cargo, asymmetric COG, 2-tonne spreader bar. All outputs update instantly on every keystroke.</p>

                    <div className="calc-layout">
                        {/* INPUT PANEL */}
                        <div>
                            <div className="inp-panel">
                                <div className="panel-header"><span className="panel-num">01</span><span className="panel-title">Cargo &amp; Spreader Bar Weight</span></div>
                                <div className="panel-body">
                                    <div className="inp-grid">
                                        <div className="inp-group">
                                            <div className="inp-lbl">W — WEIGHT OF CARGO</div>
                                            <div className="inp-row">
                                                <input type="number" id="W" value={W} onChange={e => setW(parseFloat(e.target.value) || 0)} step="0.001" />
                                                <span className="utag">t</span>
                                            </div>
                                        </div>
                                        <div className="inp-group">
                                            <div className="inp-lbl">SW — SPREADER BAR WEIGHT</div>
                                            <div className="inp-row">
                                                <input type="number" id="SW" value={SW} onChange={e => setSW(parseFloat(e.target.value) || 0)} step="0.001" />
                                                <span className="utag">t</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="inp-panel">
                                <div className="panel-header"><span className="panel-num">02</span><span className="panel-title">COG Distances from Lifting Points</span></div>
                                <div className="panel-body">
                                    <div className="inp-grid">
                                        <div className="inp-group">
                                            <div className="inp-lbl">D1 — COG LONGITUDINAL DISTANCE TO LP1/LP2 END</div>
                                            <div className="inp-row">
                                                <input type="number" id="D1" value={D1} onChange={e => handleDChange('D1', e.target.value)} step="0.001" />
                                                <span className="utag">m</span>
                                            </div>
                                        </div>
                                        <div className="inp-group">
                                            <div className="inp-lbl">D2 — COG LONGITUDINAL DISTANCE TO LP3/LP4 END</div>
                                            <div className="inp-row">
                                                <input type="number" id="D2" value={D2} onChange={e => handleDChange('D2', e.target.value)} step="0.001" />
                                                <span className="utag">m</span>
                                            </div>
                                        </div>
                                        <div className="inp-group">
                                            <div className="inp-lbl">D3 — COG LATERAL DISTANCE TO LP1/LP3 SIDE</div>
                                            <div className="inp-row">
                                                <input type="number" id="D3" value={D3} onChange={e => handleDChange('D3', e.target.value)} step="0.001" />
                                                <span className="utag">m</span>
                                            </div>
                                        </div>
                                        <div className="inp-group">
                                            <div className="inp-lbl">D4 — COG LATERAL DISTANCE TO LP2/LP4 SIDE</div>
                                            <div className="inp-row">
                                                <input type="number" id="D4" value={D4} onChange={e => handleDChange('D4', e.target.value)} step="0.001" />
                                                <span className="utag">m</span>
                                            </div>
                                        </div>
                                        <div className="inp-group">
                                            <div className="inp-lbl">DT1 — TOTAL LONGITUDINAL SPAN (D1+D2)</div>
                                            <div className="inp-row">
                                                <input type="number" id="DT1" value={DT1} onChange={e => handleDTChange('DT1', e.target.value)} step="0.001" />
                                                <span className="utag">m</span>
                                            </div>
                                        </div>
                                        <div className="inp-group">
                                            <div className="inp-lbl">DT2 — TOTAL LATERAL SPAN (D3+D4)</div>
                                            <div className="inp-row">
                                                <input type="number" id="DT2" value={DT2} onChange={e => handleDTChange('DT2', e.target.value)} step="0.001" />
                                                <span className="utag">m</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="inp-panel">
                                <div className="panel-header"><span className="panel-num">03</span><span className="panel-title">Sling Lengths</span></div>
                                <div className="panel-body">
                                    <div className="inp-grid">
                                        <div className="inp-group">
                                            <div className="inp-lbl">S1 — LOWER SLINGS LENGTH</div>
                                            <div className="inp-row">
                                                <input type="number" id="S1" value={S1} onChange={e => setS1(parseFloat(e.target.value) || 0)} step="0.001" />
                                                <span className="utag">m</span>
                                            </div>
                                        </div>
                                        <div className="inp-group">
                                            <div className="inp-lbl">S2 — UPPER SLING 5 LENGTH</div>
                                            <div className="inp-row">
                                                <input type="number" id="S2" value={S2} onChange={e => setS2(parseFloat(e.target.value) || 0)} step="0.001" />
                                                <span className="utag">m</span>
                                            </div>
                                        </div>
                                        <div className="inp-group">
                                            <div className="inp-lbl">S3 — UPPER SLING 6 LENGTH</div>
                                            <div className="inp-row">
                                                <input type="number" id="S3" value={S3} onChange={e => setS3(parseFloat(e.target.value) || 0)} step="0.001" />
                                                <span className="utag">m</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="inp-panel">
                                <div className="panel-header"><span className="panel-num">04</span><span className="panel-title">Rigging Equipment Estimate Weights</span></div>
                                <div className="panel-body">
                                    <div className="inp-grid">
                                        <div className="inp-group">
                                            <div className="inp-lbl">RW1 — UPPER RIGGING WEIGHT (LP1/LP2 END)</div>
                                            <div className="inp-row">
                                                <input type="number" id="RW1" value={RW1} onChange={e => setRW1(parseFloat(e.target.value) || 0)} step="0.001" />
                                                <span className="utag">t</span>
                                            </div>
                                        </div>
                                        <div className="inp-group">
                                            <div className="inp-lbl">RW2 — UPPER RIGGING WEIGHT (LP3/LP4 END)</div>
                                            <div className="inp-row">
                                                <input type="number" id="RW2" value={RW2} onChange={e => setRW2(parseFloat(e.target.value) || 0)} step="0.001" />
                                                <span className="utag">t</span>
                                            </div>
                                        </div>
                                        <div className="inp-group">
                                            <div className="inp-lbl">RW3 — LOWER RIGGING WEIGHT (LP1/LP2 CORNERS)</div>
                                            <div className="inp-row">
                                                <input type="number" id="RW3" value={RW3} onChange={e => setRW3(parseFloat(e.target.value) || 0)} step="0.001" />
                                                <span className="utag">t</span>
                                            </div>
                                        </div>
                                        <div className="inp-group">
                                            <div className="inp-lbl">RW4 — LOWER RIGGING WEIGHT (LP3/LP4 CORNERS)</div>
                                            <div className="inp-row">
                                                <input type="number" id="RW4" value={RW4} onChange={e => setRW4(parseFloat(e.target.value) || 0)} step="0.001" />
                                                <span className="utag">t</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* RESULTS PANEL */}
                        <div>
                            <div className="res-panel">
                                <div className="panel-header"><span className="panel-num">3D</span><span className="panel-title">Rigging System Schematic</span></div>
                                <div className="res-body" style={{ padding: '0.75rem' }}>
                                    <canvas ref={rigCanvasRef} style={{ width: '100%', height: '350px', display: 'block', background: 'var(--bg2)', borderRadius: '4px', border: '1px solid var(--border)' }}></canvas>
                                </div>
                            </div>

                            <div className="res-panel">
                                <div className="panel-header"><span className="panel-num">OUT</span><span className="panel-title">Load at Lifting Points</span></div>
                                <div className="res-body">
                                    <div className="tension-grid">
                                        <div className="t-box lower">
                                            <div className="t-box-stripe"></div>
                                            <div className="t-lbl">LOAD AT CORNER LP1 (L1)</div>
                                            <div className="t-val" id="L1">{results.L1.toFixed(3)}<span className="t-unit">t</span></div>
                                        </div>
                                        <div className="t-box lower">
                                            <div className="t-box-stripe"></div>
                                            <div className="t-lbl">LOAD AT CORNER LP2 (L2)</div>
                                            <div className="t-val" id="L2">{results.L2.toFixed(3)}<span className="t-unit">t</span></div>
                                        </div>
                                        <div className="t-box lower">
                                            <div className="t-box-stripe"></div>
                                            <div className="t-lbl">LOAD AT CORNER LP3 (L3)</div>
                                            <div className="t-val" id="L3">{results.L3.toFixed(3)}<span className="t-unit">t</span></div>
                                        </div>
                                        <div className="t-box lower">
                                            <div className="t-box-stripe"></div>
                                            <div className="t-lbl">LOAD AT CORNER LP4 (L4)</div>
                                            <div className="t-val" id="L4">{results.L4.toFixed(3)}<span className="t-unit">t</span></div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="res-panel">
                                <div className="panel-header"><span className="panel-num">OUT</span><span className="panel-title">Tension at Lower Slings</span></div>
                                <div className="res-body">
                                    <div className="tension-grid">
                                        <div className="t-box lower">
                                            <div className="t-box-stripe"></div>
                                            <div className="t-lbl">TENSION AT LOWER SLING 1 (T1)</div>
                                            <div className="t-val" id="T1">{results.T1.toFixed(3)}<span className="t-unit">t</span></div>
                                        </div>
                                        <div className="t-box lower">
                                            <div className="t-box-stripe"></div>
                                            <div className="t-lbl">TENSION AT LOWER SLING 2 (T2)</div>
                                            <div className="t-val" id="T2">{results.T2.toFixed(3)}<span className="t-unit">t</span></div>
                                        </div>
                                        <div className="t-box lower">
                                            <div className="t-box-stripe"></div>
                                            <div className="t-lbl">TENSION AT LOWER SLING 3 (T3)</div>
                                            <div className="t-val" id="T3">{results.T3.toFixed(3)}<span className="t-unit">t</span></div>
                                        </div>
                                        <div className="t-box lower">
                                            <div className="t-box-stripe"></div>
                                            <div className="t-lbl">TENSION AT LOWER SLING 4 (T4)</div>
                                            <div className="t-val" id="T4">{results.T4.toFixed(3)}<span className="t-unit">t</span></div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="res-panel">
                                <div className="panel-header"><span className="panel-num">OUT</span><span className="panel-title">Spreader Bar &amp; Height Outputs</span></div>
                                <div className="res-body">
                                    <div className="tension-grid tension-grid-3">
                                        <div className="t-box lower">
                                            <div className="t-box-stripe"></div>
                                            <div className="t-lbl">SPREADER BAR WEIGHT LP1/LP2 (SW12)</div>
                                            <div className="t-val" id="SW12">{results.SW12.toFixed(3)}<span className="t-unit">t</span></div>
                                        </div>
                                        <div className="t-box lower">
                                            <div className="t-box-stripe"></div>
                                            <div className="t-lbl">SPREADER BAR WEIGHT LP3/LP4 (SW34)</div>
                                            <div className="t-val" id="SW34">{results.SW34.toFixed(3)}<span className="t-unit">t</span></div>
                                        </div>
                                        <div className="t-box lower">
                                            <div className="t-box-stripe"></div>
                                            <div className="t-lbl">VERTICAL HEIGHT SLING 5 (H1)</div>
                                            <div className="t-val" id="H1">{results.H1.toFixed(3)}<span className="t-unit">m</span></div>
                                        </div>
                                        <div className="t-box lower">
                                            <div className="t-box-stripe"></div>
                                            <div className="t-lbl">VERTICAL HEIGHT SLING 6 (H2)</div>
                                            <div className="t-val" id="H2">{results.H2.toFixed(3)}<span className="t-unit">m</span></div>
                                        </div>
                                        <div className="t-box lower">
                                            <div className="t-box-stripe"></div>
                                            <div className="t-lbl">TOTAL VERTICAL LOAD LP1/LP2 (TW1)</div>
                                            <div className="t-val" id="TW1">{results.TW1.toFixed(3)}<span className="t-unit">t</span></div>
                                        </div>
                                        <div className="t-box lower">
                                            <div className="t-box-stripe"></div>
                                            <div className="t-lbl">TOTAL VERTICAL LOAD LP3/LP4 (TW2)</div>
                                            <div className="t-val" id="TW2">{results.TW2.toFixed(3)}<span className="t-unit">t</span></div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="res-panel">
                                <div className="panel-header"><span className="panel-num">OUT</span><span className="panel-title">Tension at Upper Slings</span></div>
                                <div className="res-body">
                                    <div className="tension-grid">
                                        <div className="t-box upper highlight">
                                            <div className="t-box-stripe"></div>
                                            <div className="t-lbl">TENSION AT UPPER SLING 5 (T5)</div>
                                            <div className="t-val" id="T5">{results.T5.toFixed(3)}<span className="t-unit">t</span></div>
                                        </div>
                                        <div className="t-box upper highlight">
                                            <div className="t-box-stripe"></div>
                                            <div className="t-lbl">TENSION AT UPPER SLING 6 (T6)</div>
                                            <div className="t-val" id="T6">{results.T6.toFixed(3)}<span className="t-unit">t</span></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* DIAGRAMS SECTION */}
            <div className="section" id="diagrams" style={{ borderTop: '1px solid var(--border)', background: 'var(--bg)' }}>
                <div style={{ maxWidth: '1300px', margin: '0 auto', padding: '5.5rem 2.5rem' }}>
                    <div className="sec-eyebrow">Visual References</div>
                    <h2 className="sec-title">Rigging System<br />Reference Diagrams</h2>
                    <p className="sec-sub">Static reference schematics extracted directly from the engineering spreadsheet model.</p>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginTop: '3rem' }}>
                        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1.5rem' }}>
                            <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)', fontSize: '12px', textTransform: 'uppercase', marginBottom: '1rem' }}>Forces &amp; Sling Tensions (Elevation View)</div>
                            <img src={image2} alt="Forces and sling tensions elevation diagram" style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '4px' }} />
                        </div>
                        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1.5rem' }}>
                            <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)', fontSize: '12px', textTransform: 'uppercase', marginBottom: '1rem' }}>Plan View &amp; COG Offsets</div>
                            <img src={image3} alt="Plan view and COG offsets diagram" style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '4px' }} />
                        </div>
                    </div>
                </div>
            </div>

            {/* FORMULAS */}
            <div className="section" id="formulas" style={{ borderTop: '1px solid var(--border)' }}>
                <div style={{ padding: '0 2.5rem' }}>
                    <div className="sec-eyebrow">Mathematical Reference</div>
                    <h2 className="sec-title">Calculation<br />Methodology</h2>
                    <p className="sec-sub">All equations follow static equilibrium principles. The 2D inverse lever rule distributes weight using moment balance in both longitudinal and lateral directions simultaneously.</p>

                    <div className="formula-grid">
                        <div className="f-card">
                            <div className="f-tag">Step 1 — Load Distribution</div>
                            <div className="f-title">Cargo Load at LP1</div>
                            <div className="f-eq">L1 = W &times; (D2 / DT1) &times; (D3 / DT2)</div>
                            <p className="f-desc">The closer a point is to the COG, the greater its share. LP1 takes load inversely proportional to D1 (longitudinal) and D4 (lateral). D2/DT1 is the longitudinal fraction; D3/DT2 is the lateral fraction.</p>
                        </div>
                        <div className="f-card">
                            <div className="f-tag">Step 1 — Load Distribution</div>
                            <div className="f-title">Cargo Loads at LP2, LP3, LP4</div>
                            <div className="f-eq">L2 = W &times; (D2/DT1) &times; (D4/DT2)<br />L3 = W &times; (D1/DT1) &times; (D4/DT2)<br />L4 = W &times; (D1/DT1) &times; (D3/DT2)</div>
                            <p className="f-desc">All four loads sum to W. L1 and L2 share the D2/DT1 factor (same longitudinal side). L1 and L4 share the D3/DT2 factor (same lateral side).</p>
                        </div>
                        <div className="f-card">
                            <div className="f-tag">Step 2 — Lower Tensions</div>
                            <div className="f-title">Lower Sling Tensions T1–T4</div>
                            <div className="f-eq">T1 = L1,  T2 = L2<br />T3 = L3,  T4 = L4</div>
                            <p className="f-desc">For vertical lower slings, tension equals the vertical load at that lifting point. This model assumes vertical lower slings, meaning tensions are exactly equal to the vertical loads.</p>
                        </div>
                        <div className="f-card">
                            <div className="f-tag">Step 3 — Spreader Bar</div>
                            <div className="f-title">Spreader Bar Weight Distribution</div>
                            <div className="f-eq">SW₁₂ = SW &times; (D2 / DT1)<br />SW₃₄ = SW &times; (D1 / DT1)</div>
                            <p className="f-desc">The spreader bar weight is distributed to each end using the same longitudinal lever rule. The side closer to the COG (D2 side) carries the larger share of the bar weight.</p>
                        </div>
                        <div className="f-card">
                            <div className="f-tag">Step 4 — Total Upper Loads</div>
                            <div className="f-title">Total Weight per Upper Sling</div>
                            <div className="f-eq">TW1 = (T1+T2) + SW₁₂ + RW1<br />TW2 = (T3+T4) + SW₃₄ + RW2</div>
                            <p className="f-desc">Each upper sling carries: the sum of lower sling tensions on its side, its proportion of the spreader bar weight, and the rigging equipment weight for the upper connection.</p>
                        </div>
                        <div className="f-card">
                            <div className="f-tag">Step 5 — Upper Tensions</div>
                            <div className="f-title">Upper Sling Tensions (Angle Factor)</div>
                            <div className="f-eq">H = &radic;(S&sup2; &minus; D&sup2;)<br />T5 = TW1 &times; (S2 / H1)<br />T6 = TW2 &times; (S3 / H2)</div>
                            <p className="f-desc">The sling angle factor S/H amplifies the vertical load into actual sling tension. H is the vertical projection of the sling. Steeper slings (small angle from vertical) have a factor near 1.0; shallower slings have larger factors and higher tension.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* SYMBOL GLOSSARY */}
            <div className="gloss-section" id="glossary">
                <div style={{ padding: '0 2.5rem' }}>
                    <div className="sec-eyebrow">Reference</div>
                    <h2 className="sec-title">Symbol<br />Glossary</h2>
                    <p className="sec-sub">Complete reference for all variables used in the 4-point spreader bar rigging calculation.</p>

                    <table className="gloss-table">
                        <thead>
                            <tr>
                                <th>Symbol</th>
                                <th>Unit</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr><td className="sym">W</td><td className="unit">t</td><td className="def">Total weight of cargo (gross weight before any rigging equipment is added)</td></tr>
                            <tr><td className="sym">SW</td><td className="unit">t</td><td className="def">Spreader bar self-weight, distributed to LP1/LP2 and LP3/LP4 sides in proportion to COG position</td></tr>
                            <tr><td className="sym">D1</td><td className="unit">m</td><td className="def">Longitudinal distance from COG to the LP1 end of the spreader bar (LP1 &amp; LP2 side)</td></tr>
                            <tr><td className="sym">D2</td><td className="unit">m</td><td className="def">Longitudinal distance from COG to the LP3 end of the spreader bar (LP3 &amp; LP4 side)</td></tr>
                            <tr><td className="sym">D3</td><td className="unit">m</td><td className="def">Lateral distance from COG to lifting points LP1 and LP3 (the D3 side)</td></tr>
                            <tr><td className="sym">D4</td><td className="unit">m</td><td className="def">Lateral distance from COG to lifting points LP2 and LP4 (the D4 side)</td></tr>
                            <tr><td className="sym">DT1</td><td className="unit">m</td><td className="def">Total longitudinal span between LP1 side and LP3 side = D1 + D2</td></tr>
                            <tr><td className="sym">DT2</td><td className="unit">m</td><td className="def">Total lateral span between LP1/LP3 side and LP2/LP4 side = D3 + D4</td></tr>
                            <tr><td className="sym">S1</td><td className="unit">m</td><td className="def">Length of each lower sling (all four lower legs S1–S4 are assumed equal in this model)</td></tr>
                            <tr><td className="sym">S2</td><td className="unit">m</td><td className="def">Length of upper Sling 5 connecting LP1/LP2 end of spreader bar to crane hook</td></tr>
                            <tr><td className="sym">S3</td><td className="unit">m</td><td className="def">Length of upper Sling 6 connecting LP3/LP4 end of spreader bar to crane hook</td></tr>
                            <tr><td className="sym">H1</td><td className="unit">m</td><td className="def">Vertical height of Sling 5 = &radic;(S2&sup2; &minus; D1&sup2;). Used to calculate the sling angle amplification factor</td></tr>
                            <tr><td className="sym">H2</td><td className="unit">m</td><td className="def">Vertical height of Sling 6 = &radic;(S3&sup2; &minus; D2&sup2;). Used to calculate the sling angle amplification factor</td></tr>
                            <tr><td className="sym">L1–L4</td><td className="unit">t</td><td className="def">Cargo weight distributed to each lifting point by the 2D inverse lever rule</td></tr>
                            <tr><td className="sym">T1–T4</td><td className="unit">t</td><td className="def">Lower sling tensions at each lifting point (= cargo load for vertical slings)</td></tr>
                            <tr><td className="sym">TW1</td><td className="unit">t</td><td className="def">Total vertical load on the LP1/LP2 side of the spreader bar = T1+T2 + SW₁₂ + RW1</td></tr>
                            <tr><td className="sym">TW2</td><td className="unit">t</td><td className="def">Total vertical load on the LP3/LP4 side of the spreader bar = T3+T4 + SW₃₄ + RW2</td></tr>
                            <tr><td className="sym">T5</td><td className="unit">t</td><td className="def">Upper Sling 5 tension = TW1 &times; (S2 / H1). The sling angle factor S/H amplifies vertical load into actual tension</td></tr>
                            <tr><td className="sym">T6</td><td className="unit">t</td><td className="def">Upper Sling 6 tension = TW2 &times; (S3 / H2)</td></tr>
                            <tr><td className="sym">RW1–RW4</td><td className="unit">t</td><td className="def">Estimated weight of rigging equipment (slings, shackles, links) per leg</td></tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}